import React, { useEffect, useRef, useState } from 'react';

interface Props {
  title:           string;
  body:            string;
  accent?:         string;
  onLearnMore?:    () => void;
  learnMoreLabel?: string;
  direction?:      'above' | 'below';
}

export default function InfoTooltip({
  title,
  body,
  accent         = '#a78bfa',
  onLearnMore,
  learnMoreLabel = 'Concept Guide',
  direction      = 'above',
}: Props): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref             = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const popupPos = direction === 'above'
    ? { bottom: 'calc(100% + 7px)', top: 'auto' }
    : { top:    'calc(100% + 7px)', bottom: 'auto' };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      <button
        onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
        title={title}
        style={{
          background:    'none',
          border:        'none',
          cursor:        'pointer',
          color:         open ? '#d8b4fe' : '#a855f7',
          fontSize:      14,
          fontWeight:    700,
          padding:       '0 3px',
          lineHeight:    1,
          fontFamily:    'monospace',
          transition:    'color 0.15s',
          userSelect:    'none',
          opacity:       1,
          flexShrink:    0,
        }}
        onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#d8b4fe'; }}
        onMouseLeave={e => { if (!open) (e.currentTarget as HTMLButtonElement).style.color = '#a855f7'; }}
      >
        ⓘ
      </button>

      {open && (
        <div style={{
          position:        'absolute',
          left:            '50%',
          transform:       'translateX(-50%)',
          zIndex:          200,
          width:           230,
          backgroundColor: '#0a0f1e',
          border:          `1px solid ${accent}44`,
          borderTop:       `2px solid ${accent}`,
          borderRadius:    8,
          padding:         '10px 13px',
          boxShadow:       `0 8px 32px rgba(0,0,0,0.5), 0 0 20px ${accent}18`,
          fontFamily:      'monospace',
          ...popupPos,
        }}>
          <p style={{ color: accent, fontSize: 8, letterSpacing: '0.18em', margin: '0 0 7px', fontWeight: 700 }}>
            {title.toUpperCase()}
          </p>
          <p style={{ color: '#94a3b8', fontSize: 10, lineHeight: 1.6, margin: 0 }}>
            {body}
          </p>
          {onLearnMore && (
            <button
              onClick={() => { setOpen(false); onLearnMore(); }}
              style={{
                background:    'none',
                border:        'none',
                cursor:        'pointer',
                color:         '#8b5cf6',
                fontSize:      9,
                padding:       '7px 0 0',
                fontFamily:    'monospace',
                letterSpacing: '0.1em',
                display:       'block',
              }}
            >
              → {learnMoreLabel} ↗
            </button>
          )}
        </div>
      )}
    </div>
  );
}
